import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { useTrial } from '../contexts/TrialContext';
import { translateMessage, translateMessageAsync } from '../utils/chatTranslation';
import '../styles/SpectatorChat.css';
import logger from '../utils/logger';

// Helper function to check if a string looks like encoded/binary data
const looksLikeEncodedData = (str) => {
  if (!str || typeof str !== 'string') return false;
  if (str.startsWith('data:')) return true;
  if (str.length > 15 && !str.includes(' ')) {
    if (/^[A-Za-z0-9+/=_-]+$/.test(str)) return true;
  }
  if (str.length > 10 && /[+/=]{2,}/.test(str)) return true;
  if (str.length > 12 && /^[a-z]+[A-Z0-9+/]+/.test(str)) return true;
  return false;
};

// Helper function to get display name
const getDisplayName = (userName) => {
  if (!userName) return 'Spectator';
  if (looksLikeEncodedData(userName)) return 'Spectator';
  return userName;
};

// Helper function to get safe avatar
const getSafeAvatar = (avatar) => {
  if (!avatar) return '👀';
  if (looksLikeEncodedData(avatar)) return '👀';
  if (avatar.length <= 4) return avatar;
  if (avatar.length > 10 && !avatar.startsWith('http')) return '👀';
  return avatar;
};

const EMOJIS = ['👀', '😮', '🔥', '👏', '😱', '🤔', '💪', '⚡', '🎯', '✨', '👑', '♟️'];

const SpectatorChat = ({ gameId }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(true); // Start collapsed
  const [isMuted, setIsMuted] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [asyncTranslations, setAsyncTranslations] = useState({});
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const { user, token } = useAuth();
  const { language } = useTranslation();
  const { hasActiveAccess } = useTrial();
  const hasPremium = hasActiveAccess();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch AI translation for a message
  const fetchTranslation = useCallback(async (msgKey, text) => {
    if (!text || asyncTranslations[msgKey]) return;
    
    try {
      const result = await translateMessageAsync(text, language);
      if (result.translated) {
        setAsyncTranslations(prev => ({
          ...prev,
          [msgKey]: result.translated
        }));
      }
    } catch (err) {
      logger.error('Translation fetch error:', err);
    }
  }, [language, asyncTranslations]);

  // Scroll to bottom when new messages arrive (only if expanded)
  useEffect(() => {
    if (!isCollapsed) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isCollapsed]);

  // Clear unread count when expanding
  useEffect(() => {
    if (!isCollapsed) {
      setUnreadCount(0);
    }
  }, [isCollapsed]);

  // Connect to spectator chat WebSocket
  useEffect(() => {
    if (!gameId || !user || !token) return;

    const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/ws/spectator-chat/${gameId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      
      // Authenticate
      ws.send(JSON.stringify({
        type: 'auth',
        token: token,
        user_id: user.user_id,
        user_name: user.name || user.email
      }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'chat_message') {
          const newMessage = {
            id: Date.now() + Math.random(),
            user: data.user_name || 'Spectator',
            userId: data.user_id,
            message: data.message,
            timestamp: new Date(data.timestamp || Date.now()),
            avatar: data.avatar || '👀'
          };
          
          setMessages(prev => [...prev, newMessage]);
          
          // Play notification sound if not muted and chat is collapsed
          if (!isMuted && isCollapsed) {
            setUnreadCount(prev => prev + 1);
            // Could add sound here: playNotificationSound();
          }
        } else if (data.type === 'system_message' || data.type === 'spectator_joined') {
          setMessages(prev => [...prev, {
            id: Date.now() + Math.random(),
            user: 'System',
            userId: 'system',
            message: data.message || `${data.user_name || 'A spectator'} joined`,
            timestamp: new Date(),
            isSystem: true
          }]);
        } else if (data.type === 'connected') {
        }
      } catch (error) {
        logger.error('Error parsing spectator chat message:', error);
      }
    };

    ws.onerror = (error) => {
      logger.error('👁️ Spectator chat error:', error);
    };

    ws.onclose = () => {
      setIsConnected(false);
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    };
  }, [gameId, user, token, API_URL]);

  const sendMessage = () => {
    if (!inputMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    wsRef.current.send(JSON.stringify({
      type: 'chat_message',
      user_id: user.user_id,
      user_name: user.name || user.email,
      message: inputMessage.trim(),
      avatar: user.avatar || '👀'
    }));

    setInputMessage('');
    setShowEmojiPicker(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const addEmoji = (emoji) => {
    setInputMessage(prev => prev + emoji);
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  // Premium lock for spectator chat
  if (!hasPremium) {
    return (
      <div className="spectator-chat-tab" data-testid="spectator-chat-tab">
        <span className="tab-icon">👁️</span>
        <span className="tab-label" style={{ color: '#d4af37' }}>Spectator Chat — Full Access</span>
      </div>
    );
  }

  // Collapsed state - just show a small tab
  if (isCollapsed) {
    return (
      <div 
        className="spectator-chat-tab" 
        data-testid="spectator-chat-tab"
      >
        <span className="tab-icon" onClick={() => setIsCollapsed(false)}>👁️</span>
        <span className="tab-label" onClick={() => setIsCollapsed(false)}>Spectator Chat</span>
        {unreadCount > 0 && (
          <span className="unread-badge" onClick={() => setIsCollapsed(false)}>{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
        <button 
          className={`tab-mute-btn ${isMuted ? 'muted' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            toggleMute();
          }}
          title={isMuted ? 'Unmute chat' : 'Mute chat'}
        >
          {isMuted ? '🔇' : '🔔'}
        </button>
        <span className={`connection-dot ${isConnected ? 'connected' : 'disconnected'}`} onClick={() => setIsCollapsed(false)} />
      </div>
    );
  }

  // Expanded state - full chat panel
  return (
    <div className="spectator-chat expanded" data-testid="spectator-chat">
      <div className="chat-header">
        <span className="chat-title">👁️ Spectator Chat</span>
        <div className="chat-controls">
          <span className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? '●' : '○'}
          </span>
          <button 
            className={`mute-btn ${isMuted ? 'muted' : ''}`}
            onClick={toggleMute}
            title={isMuted ? 'Unmute notifications' : 'Mute notifications'}
          >
            {isMuted ? '🔇' : '🔔'}
          </button>
          <button 
            className="collapse-btn" 
            onClick={() => setIsCollapsed(true)}
            title="Collapse chat"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>👀 Chat with other spectators!</p>
            <p className="hint">Players cannot see this chat</p>
          </div>
        ) : (
          messages.map((msg) => {
            const msgKey = `spec-${msg.id}`;
            const isOwnMessage = msg.userId === user?.user_id;
            
            // First try phrase dictionary translation
            const phraseTranslation = !isOwnMessage && !msg.isSystem
              ? translateMessage(msg.message, language)
              : { original: msg.message, translated: null };
            
            // Check if we have an async AI translation
            const aiTranslation = asyncTranslations[msgKey];
            
            // Use phrase translation first, then AI translation
            const finalTranslation = phraseTranslation.translated || aiTranslation;
            
            // If phrase translation didn't work and we need AI translation, fetch it
            if (!isOwnMessage && !msg.isSystem && 
                !phraseTranslation.translated && phraseTranslation.needsAiTranslation && !aiTranslation) {
              fetchTranslation(msgKey, msg.message);
            }
            
            // Sanitize avatar and username to prevent encoded data display
            const safeAvatar = getSafeAvatar(msg.avatar);
            const safeName = getDisplayName(msg.user);
            
            return (
              <div 
                key={msg.id} 
                className={`chat-message ${msg.isSystem ? 'system' : ''} ${isOwnMessage ? 'own' : ''}`}
              >
                {!msg.isSystem && (
                  <span className="message-avatar">{safeAvatar}</span>
                )}
                <div className="message-content">
                  {!msg.isSystem && (
                    <span className="message-user">{safeName}</span>
                  )}
                  <span className="message-text">{msg.message}</span>
                  {finalTranslation && finalTranslation.toLowerCase() !== msg.message.toLowerCase() && (
                    <div className="message-translation">
                      <span className="translation-label">🌐</span>
                      <span className="translated-text">{finalTranslation}</span>
                    </div>
                  )}
                  {!finalTranslation && phraseTranslation.sourceLang && phraseTranslation.sourceLang !== language && (
                    <div className="message-translation loading">
                      <span className="translation-label">🌐</span>
                      <span className="translated-text lang-indicator">Translating...</span>
                    </div>
                  )}
                  <span className="message-time">{formatTime(msg.timestamp)}</span>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        {showEmojiPicker && (
          <div className="emoji-picker">
            {EMOJIS.map((emoji) => (
              <button
                key={emoji}
                className="emoji-btn"
                onClick={() => addEmoji(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        
        <div className="chat-input-row">
          <button 
            className="emoji-toggle"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          >
            😀
          </button>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Chat with spectators..."
            maxLength={200}
            disabled={!isConnected}
          />
          <button 
            className="send-btn"
            onClick={sendMessage}
            disabled={!inputMessage.trim() || !isConnected}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpectatorChat;
